// Moteur de diagnostic offline-first.
// 1. Si le core répond → il fait foi (base régionale + IA). source = "core".
// 2. Sinon → matching local sur le cache de pannes (trigrammes). source = "local".
// Dans les deux cas, on enfile un DiagnosticEvent qui remontera au core par sync,
// et on attache le contexte board (hiérarchie de rails + checklist du symptôme).

import { DEFAULT_DOMAINE } from "../config";
import type {
  BoardModel,
  BoardSymptom,
  Cause,
  DiagnoseResult,
  DiagnosticEvent,
  Domaine,
  Fault,
} from "../domain/types";
import { CoreClient } from "../core/client";
import { addEvent, faultsByDomaine, getBoard, getEvent, updateEvent } from "../storage/db";
import { similarity } from "./localMatch";

const LOCAL_THRESHOLD = 0.3; // même seuil que diagnose_similarity_threshold du core
const TOP_N = 5;

export interface DiagnoseInput {
  symptom: string;
  domaine?: Domaine;
  device_brand?: string | null;
  device_model?: string | null;
  board_model_id?: string | null;
}

/** Agrège les causes des pannes proches, pondérées par la similarité de leur panne. */
function aggregateLocal(faults: Fault[], symptom: string): Cause[] {
  const scored = faults
    .map((f) => ({ f, s: similarity(f.symptom, symptom) }))
    .filter((x) => x.s >= LOCAL_THRESHOLD)
    .sort((a, b) => b.s - a.s)
    .slice(0, TOP_N);

  const byCause = new Map<string, Cause>();
  for (const { f, s } of scored) {
    for (const c of f.probable_causes) {
      const w = c.weight * s;
      const existing = byCause.get(c.cause);
      if (existing) {
        existing.weight = Math.min(1, existing.weight + w);
      } else {
        byCause.set(c.cause, { cause: c.cause, weight: Math.min(1, w), procedure: c.procedure ?? null });
      }
    }
  }
  return [...byCause.values()].sort((a, b) => b.weight - a.weight);
}

export async function diagnose(input: DiagnoseInput): Promise<DiagnoseResult> {
  const domaine = input.domaine ?? DEFAULT_DOMAINE;
  const symptom = input.symptom.trim();

  let causes: Cause[] = [];
  let source: "core" | "local" | "empty" = "empty";
  let faultId: string | null = null;

  // 1. Tentative core (no-op si non configuré / hors-ligne / non enrôlé)
  const client = await CoreClient.create();
  const online = client ? await client.diagnose({ domaine, symptom, device_brand: input.device_brand, device_model: input.device_model }) : null;

  if (online && online.causes.length > 0) {
    causes = online.causes;
    source = "core";
    faultId = online.fault_id ?? null;
  } else {
    // 2. Fallback local
    const faults = await faultsByDomaine(domaine);
    const filtered = input.device_model
      ? faults.filter((f) => !f.device_model || f.device_model === input.device_model)
      : faults;
    causes = aggregateLocal(filtered, symptom);
    source = causes.length > 0 ? "local" : "empty";
  }

  // 3. Enfiler l'event terrain (idempotent via client_event_id)
  const event: DiagnosticEvent = {
    client_event_id: crypto.randomUUID(),
    domaine,
    device_brand: input.device_brand ?? null,
    device_model: input.device_model ?? null,
    symptom,
    chosen_cause: null,
    resolved: false,
    board_model_id: input.board_model_id ?? null,
    occurred_at: new Date().toISOString(),
    synced: false,
  };
  await addEvent(event);

  // 4. Contexte board (rails + checklist du symptôme) depuis le KB curé
  let board: BoardModel | null = null;
  let symptomCard: BoardSymptom | null = null;
  if (input.board_model_id) {
    board = (await getBoard(input.board_model_id)) ?? null;
    if (board) {
      symptomCard =
        board.symptoms.find((s) => similarity(s.label, symptom) >= 0.3 || symptom.toLowerCase().includes(s.id)) ??
        null;
    }
  }

  return { causes, source, fault_id: faultId, event_id: event.client_event_id, board, symptom_card: symptomCard };
}

/** Retour terrain : repasse l'event en non-synchronisé pour nourrir le core. */
export async function confirmOutcome(
  eventId: string,
  chosenCause: string | null,
  resolved: boolean,
): Promise<void> {
  const ev = await getEvent(eventId);
  if (!ev) return;
  ev.chosen_cause = chosenCause;
  ev.resolved = resolved;
  ev.synced = false;
  await updateEvent(ev);
}
